import { z } from 'zod';

export const announcementSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(120),
  body: z.string().min(1).max(2000),
  important: z.boolean().default(false),
  isActive: z.boolean().default(true),
  expiresAt: z.string().datetime().nullable().default(null),
  // Emails of users who clicked "dismiss" — they hide the banner for them.
  dismissedBy: z.array(z.string().email()).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string().default(''),
});
export type Announcement = z.infer<typeof announcementSchema>;

/**
 * Legacy-tolerant read schema. The GAS frontend used different field names:
 *   `message` (we now call it `body`), `isImportant` (we now call it `important`).
 * It also never wrote `updatedAt` (see GAS index.html:3210-3243). Without
 * this preprocess, every legacy announcement fails safeParse and disappears.
 *
 * Writes go through the canonical names — this only affects reads from
 * Firestore docs created in the GAS era.
 */
export const announcementReadSchema = z.preprocess((raw) => {
  if (typeof raw !== 'object' || raw === null) return raw;
  const obj = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  if (out.body === undefined && typeof out.message === 'string') {
    out.body = out.message;
  }
  if (out.important === undefined && typeof out.isImportant === 'boolean') {
    out.important = out.isImportant;
  }
  if (out.updatedAt === undefined && typeof out.createdAt === 'string') {
    out.updatedAt = out.createdAt;
  }
  return out;
}, announcementSchema);

export const createAnnouncementSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(2000),
  important: z.boolean().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  /**
   * If true, the API also broadcasts a Slack DM to every active user at
   * creation time. Not stored on the doc — transient input.
   */
  notifySlack: z.boolean().optional().default(false),
});
export type CreateAnnouncementInput = z.infer<typeof createAnnouncementSchema>;

export const updateAnnouncementSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  body: z.string().trim().min(1).max(2000).optional(),
  important: z.boolean().optional(),
  isActive: z.boolean().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
});
export type UpdateAnnouncementInput = z.infer<typeof updateAnnouncementSchema>;
